import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";

interface User {
  username: string;
  email: string;
  status: string;
  created: string;
  last_modified: string;
  enabled: boolean;
}

const apiEndpoint = process.env.REACT_APP_API_URL;

const Users = ({ idToken }: { idToken: string }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiEndpoint}/users`, {
        headers: {
          'Authorization': idToken
        }
      });
      setUsers(response.data.users);
      setError('');
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const inviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${apiEndpoint}/users`,
        { email },
        {
          headers: {
            'Authorization': idToken,
            'Content-Type': 'application/json'
          }
        }
      );
      setSuccess(`Invitation sent to ${email}`);
      setEmail('');
      setError('');
      // Refresh the user list
      fetchUsers();
    } catch (err: any) {
      console.error('Error inviting user:', err);
      if (err.response?.status === 409) {
        setError('User already exists');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to invite users');
      } else {
        setError('Failed to send invitation');
      }
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'CONFIRMED':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'FORCE_CHANGE_PASSWORD':
        return <Clock className="h-5 w-5 text-amber-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-red-500" />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <div className="container mx-auto max-w-6xl py-6">
      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>Manage users in your organization</CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="mb-4 bg-green-50 text-green-800 border-green-200">
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>Success</AlertTitle>
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">Invite New User</h3>
            <form onSubmit={inviteUser} className="flex gap-2">
              <Input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1"
              />
              <Button type="submit" disabled={loading}>
                {loading ? 'Sending...' : 'Invite'}
              </Button>
            </form>
          </div>

          <div>
            <h3 className="text-lg font-medium mb-2">Current Users</h3>
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-4 text-gray-500">
                        {loading ? "Loading users..." : "No users found"}
                      </TableCell>
                    </TableRow>
                  )}
                  {users.map((user) => (
                    <TableRow key={user.username}>
                      <TableCell>{user.email}</TableCell>
                      <TableCell className="flex items-center gap-2">
                        {getStatusIcon(user.status)}
                        <span>{user.status}</span>
                      </TableCell>
                      <TableCell>{formatDate(user.created)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <Button variant="outline" onClick={fetchUsers} disabled={loading}>
            Refresh
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default Users;